import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Accordion, Button, Select } from '@iveco-ti/iveco-ds'
import {
  FormProvider,
  useFieldArray,
  useForm,
} from '@iveco-ti/iveco-ds/hook-form'
import { Fingerprint, Plus, Trash } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApplicationAccountUserRole } from '@/common/models/applicationAccountUser'
import { useAccountOptionsForApplicationAccountUser } from '../hooks/useAccountOptionsForApplicationAccountUser'
import { useApplicationAccountUserLimit } from '../hooks/useApplicationAccountUserLimit'
import { ApplicationWithUserAccess } from '../hooks/useManageApplicationAccountUser'
import { useNewApplicationAccountUser } from '../hooks/useNewApplicationAccountUser'
import { useRemoveApplicationAccountUser } from '../hooks/useRemoveApplicationAccountUser'
import { useRoleOptionsForApplicationAccountUser } from '../hooks/useRoleOptionsForApplicationAccountUser'
import {
  NewApplicationAccountUserForm,
  NewApplicationAccountUserSchema,
} from '../schemas/NewApplicationAccountUserSchema'

export type CardApplicationAccountUserRef = {
  resetForm: () => void
}

type CardApplicationAccountUserProps = {
  userApplication: ApplicationWithUserAccess
}

export const CardApplicationAccountUser = forwardRef<
  CardApplicationAccountUserRef,
  CardApplicationAccountUserProps
>(({ userApplication }, ref) => {
  const { t } = useTranslation('sso')
  const hookForm = useForm<NewApplicationAccountUserForm>({
    resolver: zodResolver(NewApplicationAccountUserSchema()),
    defaultValues: {
      accesses: userApplication.access.map((access) => ({
        __isNew: false,
        __id: access.id,
        accountId: access?.Account?.id,
        role: access.role,
      })),
    },
  })
  const { getAccountOptionsForUserApplicationAccount } =
    useAccountOptionsForApplicationAccountUser(userApplication)
  const { ROLE_OPTIONS, ALLOWED_ROLE_OPTIONS } =
    useRoleOptionsForApplicationAccountUser()
  const {
    handleCreateNewApplicationAccountUser,
    isCreatingNewApplicationAccountUser,
  } = useNewApplicationAccountUser()
  const { handleRemoveApplicationAccountUser } =
    useRemoveApplicationAccountUser()
  const { canAddMoreAccesses } = useApplicationAccountUserLimit(userApplication)

  const { fields } = useFieldArray<NewApplicationAccountUserForm>({
    control: hookForm.control,
    name: 'accesses',
  })

  const handleAddNewAccess = async (data: NewApplicationAccountUserForm) => {
    await handleCreateNewApplicationAccountUser(
      userApplication.application.id,
      data,
    )
  }

  useImperativeHandle(ref, () => ({
    resetForm: () => {
      hookForm.reset()
    },
  }))

  useEffect(() => {
    const accesses = userApplication.access.map((access) => ({
      __isNew: false,
      __id: access.id,
      accountId: access?.Account?.id,
      role: access.role,
    }))

    if (canAddMoreAccesses) {
      accesses.push({
        __id: '',
        __isNew: true,
        accountId: '',
        role: '' as ApplicationAccountUserRole,
      })
    }

    hookForm.setValue('accesses', accesses)
  }, [hookForm, userApplication, canAddMoreAccesses])

  return (
    <div className="border border-neutral-200 rounded-2xl space-y-4 py-4 px-5">
      <div>
        <p className="font-medium text-sm text-base-800">
          {userApplication.application.title}
        </p>
        <p className="text-sm font-light text-base-700">
          {userApplication.application.description}
        </p>
      </div>

      <FormProvider {...hookForm}>
        <Accordion.Root className="border-0 rounded-none">
          <Accordion.Item className="bg-neutral-50">
            <Accordion.Trigger>
              <Accordion.Title className="flex items-center gap-2">
                <Fingerprint size={18} className="text-brand-700" />
                <p className="font-semibold text-sm text-neutral-900">
                  {t('access.user_access_application_card.accordion.title')}
                </p>
              </Accordion.Title>

              <Accordion.Icon />
            </Accordion.Trigger>

            <Accordion.Content className="mt-2">
              <form
                onSubmit={hookForm.handleSubmit(handleAddNewAccess)}
                className="space-y-6"
              >
                {fields.length === 0 && (
                  <p className="text-sm text-neutral-600 text-center">
                    {t(
                      'access.user_access_application_card.accordion.empty_message',
                    )}
                  </p>
                )}

                {fields.map((field, index) => {
                  if (field.__isNew && userApplication.access.length !== 0) {
                    hookForm.setValue(
                      `accesses.${index}.role`,
                      userApplication.access[0].role,
                    )
                  }

                  return (
                    <div
                      key={field.id}
                      className="flex items-start gap-4 w-full"
                    >
                      {userApplication.application.requiresEntityAccount && (
                        <Select.Single
                          name={`accesses.${index}.accountId`}
                          label={t(
                            'access.user_access_application_card.fields.account.label',
                          )}
                          placeholder={t(
                            'access.user_access_application_card.fields.account.placeholder',
                          )}
                          options={getAccountOptionsForUserApplicationAccount(
                            field.accountId,
                          )}
                          isClearable={false}
                          disabled={!field.__isNew}
                        />
                      )}

                      <Select.Single
                        name={`accesses.${index}.role`}
                        label={t(
                          'access.user_access_application_card.fields.role.label',
                        )}
                        placeholder={t(
                          'access.user_access_application_card.fields.role.placeholder',
                        )}
                        options={
                          userApplication.access.length !== 0
                            ? ROLE_OPTIONS
                            : ALLOWED_ROLE_OPTIONS
                        }
                        disabled={userApplication.access.length !== 0}
                        isClearable={false}
                      />

                      {field.__isNew ? (
                        <Button.Root
                          variant="primary"
                          className="mt-7"
                          type="submit"
                          isLoading={isCreatingNewApplicationAccountUser}
                        >
                          <Plus size={16} className="text-white" />
                        </Button.Root>
                      ) : (
                        <Button.Root
                          variant="secondary"
                          className="mt-7"
                          onClick={() =>
                            handleRemoveApplicationAccountUser(field.__id!)
                          }
                        >
                          <Trash size={16} className="text-danger-600" />
                        </Button.Root>
                      )}
                    </div>
                  )
                })}
              </form>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      </FormProvider>
    </div>
  )
})

CardApplicationAccountUser.displayName = 'CardApplicationAccountUser'
